<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatFullDateTimeSP } from '../agendaDateUtils';
import {
  statusClass,
  statusBarClass,
  statusLabelKey,
  STATUS_ORDER,
  isKnownStatus,
} from '../agendaStatus';
import { avatarColor, initials } from '../agendaAvatar';

const props = defineProps({
  activeFilters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['meetingClick']);

const store = useStore();
const { t } = useI18n();

const BATCH_DAYS = 30;
// janelas vazias seguidas toleradas antes de concluir que não há mais nada
const MAX_EMPTY_BATCHES = 12;

const openStatusDropdownId = ref(null);
const statusDropdownPos = ref({ top: 0, left: 0 });
// Incrementado a cada troca de filtro; requisições de uma geração anterior
// que resolvem depois são descartadas para evitar corromper a lista.
const loadGeneration = ref(0);
const loadedMeetings = ref([]);
const oldestLoadedDate = ref(new Date());
const newestLoadedDate = ref(new Date());
const hasMorePast = ref(true);
const hasMoreFuture = ref(true);
const isLoadingPast = ref(false);
const isLoadingFuture = ref(false);
const isLoadingInitial = ref(true);
const scrollContainer = ref(null);

function statusLabel(status) {
  return isKnownStatus(status)
    ? t(`AGENDA.STATUS.${statusLabelKey(status)}`)
    : status;
}

function isPast(meeting) {
  return new Date(meeting.end_at ?? meeting.start_at) < new Date();
}

function isNow(meeting) {
  const now = new Date();
  const start = new Date(meeting.start_at);
  const end = new Date(meeting.end_at ?? meeting.start_at);
  return start <= now && now <= end;
}

const sortedMeetings = computed(() => loadedMeetings.value);

const openStatusDropdownMeeting = computed(() =>
  loadedMeetings.value.find(m => m.id === openStatusDropdownId.value)
);

function toggleStatusDropdown(meetingId, event) {
  const isOpening = openStatusDropdownId.value !== meetingId;
  openStatusDropdownId.value = isOpening ? meetingId : null;
  if (isOpening) {
    const rect = event.currentTarget.getBoundingClientRect();
    statusDropdownPos.value = { top: rect.bottom + 4, left: rect.left };
  }
}

async function selectStatus(meeting, status) {
  openStatusDropdownId.value = null;
  if (status === meeting.status) return;

  const updated = await store.dispatch('agenda/updateMeetingField', {
    id: meeting.id,
    status,
  });

  const idx = loadedMeetings.value.findIndex(m => m.id === meeting.id);
  if (idx !== -1 && updated) {
    loadedMeetings.value[idx] = { ...loadedMeetings.value[idx], ...updated };
  }
}

function onClickOutside(e) {
  if (!e.target.closest('[data-status-dropdown]')) {
    openStatusDropdownId.value = null;
  }
}

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside));

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

async function fetchRange(startFrom, startTo) {
  const params = {
    start_from: startFrom.toISOString(),
    start_to: startTo.toISOString(),
    ...props.activeFilters,
  };
  const { data } = await store.dispatch('agenda/fetchMeetingsRaw', params);
  return data;
}

// uma janela vazia (férias, mês sem reuniões) não significa fim da lista:
// se vier vazia, busca de uma vez o resto do horizonte (no máximo 2 requests)
async function fetchBatch(edge, direction) {
  const windowEdge = addDays(edge, BATCH_DAYS * direction);
  const horizonEdge = addDays(edge, BATCH_DAYS * MAX_EMPTY_BATCHES * direction);
  const range = (a, b) => (direction < 0 ? [b, a] : [a, b]);

  const data = await fetchRange(...range(edge, windowEdge));
  if (data.length) return { data, cursor: windowEdge, exhausted: false };

  const rest = await fetchRange(...range(windowEdge, horizonEdge));
  return { data: rest, cursor: horizonEdge, exhausted: !rest.length };
}

function scrollToNow() {
  if (!scrollContainer.value) return;
  const nowEl = scrollContainer.value.querySelector('[data-now-row]');
  const firstFutureEl = scrollContainer.value.querySelector(
    '[data-future-row="true"]'
  );
  const target = nowEl ?? firstFutureEl;
  if (target) {
    target.scrollIntoView({ block: 'center' });
  }
}

async function loadInitial() {
  const generation = loadGeneration.value;
  const now = new Date();
  const [past, future] = await Promise.all([
    fetchBatch(now, -1),
    fetchBatch(now, 1),
  ]);

  if (generation !== loadGeneration.value) return;

  hasMorePast.value = !past.exhausted;
  hasMoreFuture.value = !future.exhausted;
  oldestLoadedDate.value = past.cursor;
  newestLoadedDate.value = future.cursor;

  loadedMeetings.value = [...past.data, ...future.data].sort(
    (a, b) => new Date(b.start_at) - new Date(a.start_at)
  );

  isLoadingInitial.value = false;

  await nextTick();
  scrollToNow();
  // eslint-disable-next-line no-use-before-define
  fillViewport();
}

async function loadMorePast() {
  if (isLoadingPast.value || !hasMorePast.value) return;
  isLoadingPast.value = true;
  const generation = loadGeneration.value;

  const {
    data: older,
    cursor,
    exhausted,
  } = await fetchBatch(oldestLoadedDate.value, -1);

  if (generation !== loadGeneration.value) {
    isLoadingPast.value = false;
    return;
  }

  hasMorePast.value = !exhausted;
  oldestLoadedDate.value = cursor;

  if (older.length) {
    loadedMeetings.value = [...loadedMeetings.value, ...[...older].reverse()];
  }

  isLoadingPast.value = false;
  // eslint-disable-next-line no-use-before-define
  fillViewport();
}

// sem barra de rolagem o onScroll nunca dispara: com poucas reuniões a lista
// parava na primeira janela. Continua buscando as antigas até preencher a tela.
async function fillViewport() {
  await nextTick();
  const el = scrollContainer.value;
  if (el && el.scrollHeight <= el.clientHeight) loadMorePast();
}

async function loadMoreFuture() {
  if (isLoadingFuture.value || !hasMoreFuture.value) return;
  isLoadingFuture.value = true;
  const generation = loadGeneration.value;

  const container = scrollContainer.value;
  const prevScrollHeight = container?.scrollHeight ?? 0;

  const {
    data: newer,
    cursor,
    exhausted,
  } = await fetchBatch(newestLoadedDate.value, 1);

  if (generation !== loadGeneration.value) {
    isLoadingFuture.value = false;
    return;
  }

  hasMoreFuture.value = !exhausted;
  newestLoadedDate.value = cursor;

  if (newer.length) {
    loadedMeetings.value = [...[...newer].reverse(), ...loadedMeetings.value];
    await nextTick();
    if (container) {
      const newScrollHeight = container.scrollHeight;
      container.scrollTop += newScrollHeight - prevScrollHeight;
    }
  }

  isLoadingFuture.value = false;
}

function onScroll(e) {
  const el = e.target;
  if (el.scrollTop < 100) {
    loadMoreFuture();
  }
  if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
    loadMorePast();
  }
}

watch(
  () => props.activeFilters,
  () => {
    loadGeneration.value += 1;
    loadedMeetings.value = [];
    hasMorePast.value = true;
    hasMoreFuture.value = true;
    isLoadingInitial.value = true;
    loadInitial();
  },
  { deep: true }
);

onMounted(loadInitial);
</script>

<template>
  <div class="relative flex-1 overflow-hidden">
    <div
      v-if="isLoadingInitial"
      class="absolute inset-0 bg-n-background/60 flex items-center justify-center z-20"
    >
      <span class="i-lucide-loader-2 size-6 text-n-brand animate-spin" />
    </div>

    <div
      ref="scrollContainer"
      class="h-full overflow-y-auto"
      @scroll="onScroll"
    >
      <!-- Desktop: tabela -->
      <table class="hidden md:table w-full text-sm">
        <thead class="sticky top-0 bg-n-background border-b border-n-weak z-10">
          <tr class="text-left text-n-slate-11">
            <th class="w-1 p-0" />
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_TITLE') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_CALENDAR') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_TIME') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_STATUS') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_CONTACT') }}
            </th>
            <th class="px-4 py-3 font-medium">
              {{ t('AGENDA.LIST_VIEW.COLUMN_LINK') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="meeting in sortedMeetings"
            :key="meeting.id"
            :data-now-row="isNow(meeting) || undefined"
            :data-future-row="!isPast(meeting) && !isNow(meeting)"
            class="border-b border-n-weak cursor-pointer hover:bg-n-alpha-1 transition-colors"
            :class="[isNow(meeting) ? 'bg-n-blue-2' : '']"
            @click="emit('meetingClick', meeting.id)"
          >
            <td class="p-0 w-1 relative">
              <span
                class="absolute inset-y-0 left-0 w-1 rounded-sm"
                :class="statusBarClass(meeting.status)"
              />
            </td>
            <td class="px-4 py-3" :class="isPast(meeting) ? 'opacity-60' : ''">
              <div class="flex items-center gap-2 min-w-0">
                <span
                  class="i-lucide-calendar size-4 text-n-slate-10 flex-shrink-0"
                />
                <span class="truncate font-medium text-n-slate-12">{{
                  meeting.title
                }}</span>
              </div>
            </td>
            <td
              class="px-4 py-3 text-n-slate-11 truncate"
              :class="isPast(meeting) ? 'opacity-60' : ''"
            >
              <div class="flex items-center gap-1.5 min-w-0">
                <span
                  class="size-2 rounded-full inline-block flex-shrink-0"
                  :style="{ backgroundColor: meeting.user?.color }"
                />
                <span class="truncate">{{ meeting.user?.name ?? '' }}</span>
              </div>
            </td>
            <td
              class="px-4 py-3 text-n-slate-11 whitespace-nowrap"
              :class="isPast(meeting) ? 'opacity-60' : ''"
            >
              {{ formatFullDateTimeSP(meeting.start_at) }}
            </td>
            <td class="px-4 py-3" data-status-dropdown @click.stop>
              <button
                class="text-xs px-2.5 py-1 rounded-full border font-medium inline-flex items-center whitespace-nowrap"
                :class="statusClass(meeting.status)"
                @click="toggleStatusDropdown(meeting.id, $event)"
              >
                {{ statusLabel(meeting.status) }}
                <span class="i-lucide-chevron-down size-3 ml-1 inline-block" />
              </button>
            </td>
            <td class="px-4 py-3" :class="isPast(meeting) ? 'opacity-60' : ''">
              <div
                v-if="meeting.contact?.name"
                class="flex items-center gap-2 min-w-0"
              >
                <div
                  class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
                  :style="{
                    backgroundColor: avatarColor(meeting.contact.name),
                  }"
                >
                  {{ initials(meeting.contact.name) }}
                </div>
                <span class="truncate text-n-slate-12">{{
                  meeting.contact.name
                }}</span>
              </div>
            </td>
            <td class="px-4 py-3" :class="isPast(meeting) ? 'opacity-60' : ''">
              <a
                v-if="meeting.google_meet_url"
                :href="meeting.google_meet_url"
                target="_blank"
                rel="noopener noreferrer"
                class="i-logos-google-meet size-4 inline-block hover:opacity-80"
                @click.stop
              />
              <span
                v-else
                class="i-logos-google-meet size-4 inline-block opacity-30"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Mobile: cards empilhados -->
      <div class="md:hidden divide-y divide-n-weak pb-20">
        <div
          v-for="meeting in sortedMeetings"
          :key="meeting.id"
          :data-now-row="isNow(meeting) || undefined"
          :data-future-row="!isPast(meeting) && !isNow(meeting)"
          class="relative pl-5 pr-4 py-3 cursor-pointer active:bg-n-alpha-1 transition-colors"
          :class="[isNow(meeting) ? 'bg-n-blue-2' : '']"
          @click="emit('meetingClick', meeting.id)"
        >
          <span
            class="absolute left-1 top-1 bottom-1 w-1 rounded-sm"
            :class="statusBarClass(meeting.status)"
          />
          <div
            class="flex items-center gap-2 min-w-0"
            :class="isPast(meeting) ? 'opacity-60' : ''"
          >
            <span
              class="i-lucide-calendar size-4 text-n-slate-10 flex-shrink-0"
            />
            <span class="truncate font-medium text-n-slate-12">{{
              meeting.title
            }}</span>
            <a
              v-if="meeting.google_meet_url"
              :href="meeting.google_meet_url"
              target="_blank"
              rel="noopener noreferrer"
              class="i-logos-google-meet size-4 flex-shrink-0 hover:opacity-80"
              @click.stop
            />
          </div>

          <div
            class="flex items-center justify-between gap-2 mt-2"
            :class="isPast(meeting) ? 'opacity-60' : ''"
          >
            <div
              v-if="meeting.contact?.name"
              class="flex items-center gap-2 min-w-0"
            >
              <div
                class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
                :style="{ backgroundColor: avatarColor(meeting.contact.name) }"
              >
                {{ initials(meeting.contact.name) }}
              </div>
              <span class="truncate text-sm text-n-slate-12">{{
                meeting.contact.name
              }}</span>
            </div>
            <span
              v-if="meeting.user?.name"
              class="text-xs text-n-slate-11 truncate flex-shrink-0"
            >
              {{ meeting.user.name }}
            </span>
          </div>

          <div class="flex items-center justify-between gap-2 mt-2">
            <p
              class="text-xs text-n-slate-11"
              :class="isPast(meeting) ? 'opacity-60' : ''"
            >
              {{ formatFullDateTimeSP(meeting.start_at) }}
            </p>
            <div data-status-dropdown @click.stop>
              <button
                class="text-xs px-2.5 py-1 rounded-full border font-medium inline-flex items-center whitespace-nowrap"
                :class="statusClass(meeting.status)"
                @click="toggleStatusDropdown(meeting.id, $event)"
              >
                {{ statusLabel(meeting.status) }}
                <span class="i-lucide-chevron-down size-3 ml-1 inline-block" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Dropdown de status (compartilhado desktop/mobile via Teleport) -->
      <Teleport to="body">
        <div
          v-if="openStatusDropdownId !== null"
          data-status-dropdown
          class="fixed z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 w-40"
          :style="{
            top: statusDropdownPos.top + 'px',
            left: statusDropdownPos.left + 'px',
          }"
          @mousedown.stop
          @click.stop
        >
          <button
            v-for="status in STATUS_ORDER"
            :key="status"
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              status === openStatusDropdownMeeting?.status
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="selectStatus(openStatusDropdownMeeting, status)"
          >
            {{ statusLabel(status) }}
            <span
              v-if="status === openStatusDropdownMeeting?.status"
              class="i-lucide-check size-3 text-n-brand ml-auto inline-block"
            />
          </button>
        </div>
      </Teleport>

      <p
        v-if="!sortedMeetings.length && !isLoadingInitial"
        class="text-center text-sm text-n-slate-10 py-10"
      >
        {{ t('AGENDA.LIST_VIEW.NO_MEETINGS') }}
      </p>
    </div>
  </div>
</template>
